/**
 * Source Material & Regulatory Registry URL Resolver.
 * Resolves direct, verifiable public links to County Deed Portals,
 * SEC EDGAR corporate filings, State Statutory Economic Codes, and ISO/RTO Queue Portals.
 */

export interface SourceLink {
  url: string;
  label: string;
  sourceType: 'COUNTY_CLERK' | 'SEC_EDGAR' | 'STATE_STATUTE' | 'ISO_QUEUE' | 'REGULATORY';
  badgeText: string;
}

// 1. County Land Records Portals
const KNOWN_COUNTY_RECORDERS: Record<string, string> = {
  // Virginia
  'LOUDOUN_VA': 'https://www.loudoun.gov/landrecords',
  'PRINCE WILLIAM_VA': 'https://www.pwcva.gov/department/circuit-court/land-records',
  'FAIRFAX_VA': 'https://www.fairfaxcounty.gov/circuit/land-records',
  'HENRICO_VA': 'https://henrico.gov/circuitclerk/land-records/',
  'CULPEPER_VA': 'https://webgis.culpepercounty.gov/',
  'FAUQUIER_VA': 'https://www.fauquiercounty.gov/government/departments-a-g/circuit-court-clerk/land-records',
  'SPOTSYLVANIA_VA': 'https://www.spotsylvania.va.us/182/Circuit-Court-Clerk',

  // Ohio
  'FRANKLIN_OH': 'https://recorder.franklincountyohio.gov/',
  'LICKING_OH': 'https://lickingcounty.gov/depts/recorder/',
  'DELAWARE_OH': 'https://recorder.co.delaware.oh.us/',
  'PICKAWAY_OH': 'https://www.pickaway.org/offices/recorder/',
  'UNION_OH': 'https://www.co.union.oh.us/recorder',
  'MADISON_OH': 'https://www.co.madison.oh.us/departments/recorder/index.php',

  // Texas
  'DALLAS_TX': 'https://www.dallascounty.org/government/county-clerk/recording/',
  'TARRANT_TX': 'https://access.tarrantcounty.com/en/county-clerk.html',
  'HARRIS_TX': 'https://www.cclerk.hctx.net/',
  'TRAVIS_TX': 'https://www.countyclerk.traviscountytx.gov/departments/recording/',
  'BEXAR_TX': 'https://www.bexar.org/2946/County-Clerk',
  'DENTON_TX': 'https://www.dentoncounty.gov/306/County-Clerk',
  'COLLIN_TX': 'https://www.collincountytx.gov/county_clerk/',
  'ELLIS_TX': 'https://co.ellis.tx.us/112/County-Clerk',
  'WILIAMSON_TX': 'https://www.wilco.org/county-clerk',

  // Arizona
  'MARICOPA_AZ': 'https://recorder.maricopa.gov/',
  'PINAL_AZ': 'https://www.pinalcountyaz.gov/Recorder/Pages/Home.aspx',

  // Georgia
  'FULTON_GA': 'https://www.fultoncountyga.gov/inside-fulton-county/fulton-county-departments/superior-and-state-court/deeds-and-records',
  'DOUGLAS_GA': 'https://www.celebratedouglascounty.com/',
  'GWINNETT_GA': 'https://www.gwinnettcourts.com/clerk/deed-and-land-records',

  // Illinois
  'COOK_IL': 'https://cookcountyclerkil.gov/recordings',
  'DUPAGE_IL': 'https://www.dupagecounty.gov/recorder/',
  'KANE_IL': 'https://www.kanecountyrecorder.net/',
  'WILL_IL': 'https://willcountyrecorder.gov/',

  // Indiana
  'BOONE_IN': 'https://boonecounty.in.gov/recorder/',
  'MARION_IN': 'https://indy.gov/agency/marion-county-recorder',
  'LAPORTE_IN': 'https://www.laportecounty.in.gov/departments/recorder/',

  // Nevada
  'CLARK_NV': 'https://www.clarkcountynv.gov/government/assessor/',
  'STOREY_NV': 'https://www.storeycounty.org/168/Recorder',

  // North Carolina
  'MECKLENBURG_NC': 'https://www.mecknc.gov/ROD/Pages/default.aspx',
  'WAKE_NC': 'https://www.wake.gov/departments-government/register-deeds',

  // South Carolina
  'BERKELEY_SC': 'https://berkeleycountysc.gov/dept/rod/',
  'RICHLAND_SC': 'https://www.richlandcountysc.gov/Government/Departments/Taxes/Register-of-Deeds',

  // California
  'SANTA CLARA_CA': 'https://clerkrecorder.sccgov.org/',
  'RIVERSIDE_CA': 'https://www.countyclerk.riversidecountyme.gov/',
};

/**
 * Returns direct County Deed Registry URL or official recorder clerk search.
 */
export function getCountyRecorderUrl(
  county?: string | null,
  state?: string | null,
  instrumentNumber?: string | null
): SourceLink {
  const safeCounty = (county || '').trim();
  const safeState = (state || '').trim();
  const key = `${safeCounty.toUpperCase()}_${safeState.toUpperCase()}`;
  const directPortal = KNOWN_COUNTY_RECORDERS[key];

  if (directPortal) {
    return {
      url: directPortal,
      label: `${safeCounty || 'County'} Recorder Land Records`,
      sourceType: 'COUNTY_CLERK',
      badgeText: instrumentNumber ? `Doc #${instrumentNumber}` : 'Deed Registry',
    };
  }

  // Fallback to targeted search for the exact county recorder portal
  const query = `${safeCounty} County ${safeState} county clerk recorder land records deeds ${instrumentNumber || ''}`.trim();
  return {
    url: `https://www.google.com/search?q=${encodeURIComponent(query || 'county recorder land records')}`,
    label: `${safeCounty || 'County'}, ${safeState || 'State'} Land Records Portal`,
    sourceType: 'COUNTY_CLERK',
    badgeText: instrumentNumber ? `Doc #${instrumentNumber}` : 'Deed Registry',
  };
}

// 2. SEC EDGAR CIKs for hyperscalers and data center REITs
const KNOWN_CIKS: Record<string, { cik: string; name: string }> = {
  'MICROSOFT': { cik: '0000789019', name: 'Microsoft Corp' },
  'AMAZON': { cik: '0001018724', name: 'Amazon.com Inc' },
  'ALPHABET': { cik: '0001652044', name: 'Alphabet Inc' },
  'GOOGLE': { cik: '0001652044', name: 'Alphabet Inc' },
  'META': { cik: '0001326801', name: 'Meta Platforms Inc' },
  'EQUINIX': { cik: '0001101239', name: 'Equinix Inc' },
  'DIGITAL REALTY': { cik: '0001040971', name: 'Digital Realty Trust Inc' },
  'IRON MOUNTAIN': { cik: '0001020569', name: 'Iron Mountain Inc' },
  'AMERICAN TOWER': { cik: '0001053507', name: 'American Tower Corp' },
  'NVIDIA': { cik: '0001045810', name: 'NVIDIA Corp' },
  'ORACLE': { cik: '0001341439', name: 'Oracle Corp' },
  'APPLE': { cik: '0000320193', name: 'Apple Inc' },
};

/**
 * Returns direct SEC EDGAR browse URL for corporate lease or public REIT disclosures.
 */
export function getSecEdgarUrl(cik?: string | null, entityName?: string | null): SourceLink {
  if (cik && cik.trim()) {
    const cleanCik = cik.trim().padStart(10, '0');
    return {
      url: `https://www.sec.gov/edgar/browse/?CIK=${cleanCik}`,
      label: `SEC EDGAR Company Filings (CIK: ${cleanCik})`,
      sourceType: 'SEC_EDGAR',
      badgeText: `SEC CIK ${cleanCik}`,
    };
  }

  if (entityName) {
    const upper = entityName.toUpperCase();
    for (const [key, val] of Object.entries(KNOWN_CIKS)) {
      if (upper.includes(key)) {
        return {
          url: `https://www.sec.gov/edgar/browse/?CIK=${val.cik}`,
          label: `SEC EDGAR Filings (${val.name})`,
          sourceType: 'SEC_EDGAR',
          badgeText: `SEC EDGAR (${val.cik})`,
        };
      }
    }
  }

  return {
    url: 'https://www.sec.gov/edgar/searchedgar/companysearch',
    label: 'SEC EDGAR Public Filings Search',
    sourceType: 'SEC_EDGAR',
    badgeText: 'SEC EDGAR',
  };
}

/**
 * Returns direct state statutory economic development law link for PILOT abatements.
 */
export function getStatutoryAuthorityUrl(
  statutoryAuthority?: string | null,
  jurisdictionName?: string | null,
  state?: string | null
): SourceLink {
  const auth = (statutoryAuthority || '').toUpperCase();
  const st = (state || '').toUpperCase();

  // Virginia
  if (st === 'VA' || auth.includes('58.1') || auth.includes('VIRGINIA')) {
    return {
      url: 'https://law.lis.virginia.gov/vacode/title58.1/chapter6/section58.1-609.3/',
      label: 'Virginia Code § 58.1-609.3 (Data Center Sales & Use Tax Exemption)',
      sourceType: 'STATE_STATUTE',
      badgeText: 'VA Code § 58.1-609.3',
    };
  }

  // Ohio
  if (st === 'OH' || auth.includes('5709') || auth.includes('OHIO') || auth.includes('ORC')) {
    return {
      url: 'https://codes.ohio.gov/ohio-revised-code/section-5709.62',
      label: 'Ohio Revised Code § 5709.62 (Enterprise Zone & Data Center Exemption)',
      sourceType: 'STATE_STATUTE',
      badgeText: 'OH ORC § 5709.62',
    };
  }

  // Texas
  if (st === 'TX' || auth.includes('312') || auth.includes('313') || auth.includes('TEXAS')) {
    return {
      url: 'https://statutes.capitol.texas.gov/Docs/TX/htm/TX.312.htm',
      label: 'Texas Tax Code Chapter 312 (Property Redevelopment and Tax Abatement Act)',
      sourceType: 'STATE_STATUTE',
      badgeText: 'TX Tax Code Ch. 312',
    };
  }

  // Illinois
  if (st === 'IL' || auth.includes('ILLINOIS') || auth.includes('DCEO')) {
    return {
      url: 'https://dceo.illinois.gov/expandrelocate/incentives/datacenterinvestmentprogram.html',
      label: 'Illinois Data Center Investment Program (35 ILCS 105/3-5)',
      sourceType: 'STATE_STATUTE',
      badgeText: '35 ILCS 105/3-5',
    };
  }

  // Georgia
  if (st === 'GA' || auth.includes('GEORGIA')) {
    return {
      url: 'https://dor.georgia.gov/high-technology-data-center-exemption',
      label: 'Georgia Code § 48-8-3(68) (High-Technology Data Center Exemption)',
      sourceType: 'STATE_STATUTE',
      badgeText: 'GA Code § 48-8-3',
    };
  }

  // Arizona
  if (st === 'AZ' || auth.includes('ARIZONA') || auth.includes('41-1519')) {
    return {
      url: 'https://www.azcommerce.com/incentives/computer-data-center-program/',
      label: 'Arizona A.R.S. § 41-1519 (Computer Data Center Incentive Program)',
      sourceType: 'STATE_STATUTE',
      badgeText: 'A.R.S. § 41-1519',
    };
  }

  // Indiana
  if (st === 'IN' || auth.includes('INDIANA')) {
    return {
      url: 'https://iga.in.gov/laws/2024/ic/titles/6#6-2.5-5-53',
      label: 'Indiana Code § 6-2.5-5-53 (Enterprise Information Technology Equipment)',
      sourceType: 'STATE_STATUTE',
      badgeText: 'IC 6-2.5-5-53',
    };
  }

  const query = `${jurisdictionName || ''} ${state || ''} ${statutoryAuthority || 'data center tax incentive PILOT agreement statutory authority'}`;
  return {
    url: `https://www.google.com/search?q=${encodeURIComponent(query)}`,
    label: statutoryAuthority || `${jurisdictionName || 'Municipal'} Statutory PILOT Authority`,
    sourceType: 'STATE_STATUTE',
    badgeText: 'Statutory PILOT Law',
  };
}

/**
 * Returns direct ISO / RTO Interconnection Queue Registry URL.
 */
export function getIsoQueuePortalUrl(iso: string, queueRequestId?: string | null): SourceLink {
  const normIso = (iso || 'PJM').toUpperCase().trim();

  switch (normIso) {
    case 'PJM':
      return {
        url: 'https://www.pjm.com/planning/services-requests/interconnection-queues.aspx',
        label: `PJM Interconnection Queue Portal ${queueRequestId ? `(#${queueRequestId})` : ''}`,
        sourceType: 'ISO_QUEUE',
        badgeText: queueRequestId ? `PJM ${queueRequestId}` : 'PJM Queue',
      };
    case 'ERCOT':
      return {
        url: 'https://www.ercot.com/gridinfo/generation',
        label: `ERCOT Generation Interconnection Tracking ${queueRequestId ? `(#${queueRequestId})` : ''}`,
        sourceType: 'ISO_QUEUE',
        badgeText: queueRequestId ? `ERCOT ${queueRequestId}` : 'ERCOT Queue',
      };
    case 'MISO':
      return {
        url: 'https://www.misoenergy.org/planning/generator-interconnection/GI_Queue/',
        label: `MISO Generator Interconnection Queue ${queueRequestId ? `(#${queueRequestId})` : ''}`,
        sourceType: 'ISO_QUEUE',
        badgeText: queueRequestId ? `MISO ${queueRequestId}` : 'MISO Queue',
      };
    case 'NYISO':
      return {
        url: 'https://www.nyiso.com/interconnections',
        label: `NYISO Interconnection Queue Studies ${queueRequestId ? `(#${queueRequestId})` : ''}`,
        sourceType: 'ISO_QUEUE',
        badgeText: queueRequestId ? `NYISO ${queueRequestId}` : 'NYISO Queue',
      };
    case 'CAISO':
      return {
        url: 'https://www.caiso.com/planning/Pages/TransmissionPlanning/InterconnectionQueue.aspx',
        label: `CAISO Interconnection Queue Registry ${queueRequestId ? `(#${queueRequestId})` : ''}`,
        sourceType: 'ISO_QUEUE',
        badgeText: queueRequestId ? `CAISO ${queueRequestId}` : 'CAISO Queue',
      };
    default:
      return {
        url: 'https://www.ferc.gov/electric/industry-activities/generator-interconnection',
        label: `FERC Regional Queue Docket ${queueRequestId ? `(#${queueRequestId})` : ''}`,
        sourceType: 'ISO_QUEUE',
        badgeText: queueRequestId || normIso,
      };
  }
}
